import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, Handshake, Loader2, AlertTriangle, Clock, Check } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR2, fmtDate, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import MarkReceivedDialog from "@/components/MarkReceivedDialog";
import { LocationsEditor } from "@/components/praticien/LocationsEditor";
import { RetroSplit, SensBadge } from "@/components/retro/RetroSplit";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const now = new Date();

export default function Retrocessions() {
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ ca_non_attribue: 0, ca_en_attente_total: 0 });
  const [prats, setPrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [receiveTx, setReceiveTx] = useState(null);

  const load = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get(`/retrocessions?year=${ym.year}&month=${ym.month}`),
        api.get("/practitioners"),
      ]);
      setRows(r.data.practitioners || []);
      setMeta({ ca_non_attribue: r.data.ca_non_attribue || 0, ca_en_attente_total: r.data.ca_en_attente_total || 0 });
      setPrats(p.data);
    } finally { setLoading(false); }
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const open = (p) => { setEditing(p); setF(p ? { ...p, encaisse_direct: p.encaisse_direct ?? p.encaisse_direct_mode ?? true } : { name: "", taux: 60, frais_fixes: 0, deductions: 0, encaisse_direct: true }); setDialog(true); };
  const save = async () => {
    if (!f.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/practitioners/${editing.id}`, f);
      else await api.post("/practitioners", f);
      toast.success("Enregistré"); setDialog(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/practitioners/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Praticiens partenaires</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Rétrocessions</h1>
        </div>
        <Button data-testid="add-practitioner-btn" onClick={() => open(null)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Praticien</Button>
      </div>

      <div className="mt-4">
        <Select value={`${ym.year}-${ym.month}`} onValueChange={(v) => { const [y, m] = v.split("-"); setYm({ year: +y, month: +m }); }}>
          <SelectTrigger className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 15 }).map((_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
              return <SelectItem key={i} value={`${d.getFullYear()}-${d.getMonth() + 1}`}>{MONTHS_FR[d.getMonth() + 1]} {d.getFullYear()}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {(meta.ca_non_attribue > 0 || meta.ca_en_attente_total > 0) && (
        <div className="mt-4 space-y-2" data-testid="retro-meta">
          {meta.ca_non_attribue > 0 && (
            <div className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="unassigned-ca">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span><b>{fmtEUR2(meta.ca_non_attribue)}</b> de CA encaissé non attribué à un praticien ce mois-ci — vérifiez les saisies sans praticien pour qu'elles entrent dans les rétrocessions.</span>
            </div>
          )}
          {meta.ca_en_attente_total > 0 && (
            <div className="flex items-start gap-2 rounded-2xl bg-sky-50 p-3 text-sm text-sky-800" data-testid="pending-ca">
              <Clock className="mt-0.5 h-4 w-4 shrink-0" />
              <span><b>{fmtEUR2(meta.ca_en_attente_total)}</b> de CA en attente de virement (Sécu/C2S, mutuelles, Alma) — non rétrocédé tant qu'il n'est pas perçu, puis reporté sur le mois de sa réception.</span>
            </div>
          )}
        </div>
      )}

      {loading ? <Loader /> : (
        <div className="mt-5 space-y-5">
          <div className="space-y-4">
            {rows.length === 0 && <EmptyState title="Aucune rétrocession calculée" subtitle="Ajoutez un praticien avec un taux et associez son CA." />}
            {rows.map((r) => (
              <Collapsible key={r.practitioner.id} className={`${CARD} p-6`} data-testid={`retro-${r.practitioner.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-50 text-violet-600"><Handshake className="h-5 w-5" /></span>
                    <div>
                      <p className="font-semibold">{r.practitioner.name}</p>
                      <p className="text-xs text-gray-400">Taux {r.taux}% · CA {fmtEUR2(r.ca)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Solde à ce jour</p>
                    <p className="font-display text-xl font-black text-violet-700" data-testid={`retro-net-${r.practitioner.id}`}>{fmtEUR2(Math.abs(r.net))}</p>
                    <SensBadge value={r.net} />
                  </div>
                </div>
                <CollapsibleTrigger data-testid={`retro-detail-${r.practitioner.id}`} className="mt-3 flex items-center gap-1 text-sm font-semibold text-[#065F46]">
                  Voir le détail <ChevronDown className="h-4 w-4" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-3"><RetroSplit r={r} year={ym.year} month={ym.month} onChanged={() => load(true)} /></div>
                  {r.pending && r.pending.length > 0 && (
                    <div className="mt-3 space-y-2 rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Virements en attente</p>
                      {r.pending.map((pt) => (
                        <div key={pt.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`retro-pending-${pt.id}`}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${{ secu: "bg-sky-100 text-sky-700", mutuelle: "bg-violet-100 text-violet-700", alma: "bg-amber-100 text-amber-700" }[pt.revenue_kind]}`}>{{ secu: "Sécu / CMU", mutuelle: "Mutuelle", alma: "Alma" }[pt.revenue_kind]}</span>
                          <span className="flex-1 text-gray-500">prévu le {fmtDate(pt.transaction_date)}</span>
                          <b>{fmtEUR2(pt.amount)}</b>
                          <Button data-testid={`retro-receive-${pt.id}`} size="sm" variant="outline" onClick={() => setReceiveTx(pt)} className="rounded-full"><Check className="mr-1 h-3.5 w-3.5" />Marquer reçu</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>

          <div className={`${CARD} p-4 sm:p-6`}>
            <Label className="mb-2">Règles de rétrocession</Label>
            {prats.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-gray-50 py-3 last:border-0">
                <div><p className="text-sm font-semibold">{p.name}</p><p className="text-xs text-gray-400">Taux {p.taux}% · déductions {fmtEUR2((p.frais_fixes || 0) + (p.deductions || 0))}{p.locations?.length ? ` · ${p.locations.map((l) => l.piece).join(", ")}` : ""}</p></div>
                <div className="flex gap-1">
                  <button data-testid={`edit-prat-${p.id}`} onClick={() => open(p)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p.id)} className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="practitioner-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Modifier" : "Nouveau praticien"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><UiLabel>Nom</UiLabel><Input data-testid="prat-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><UiLabel>Taux de rétrocession (%)</UiLabel><Input data-testid="prat-taux" type="number" value={f.taux ?? ""} onChange={(e) => setF({ ...f, taux: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><UiLabel>Frais fixes (€)</UiLabel><Input type="number" value={f.frais_fixes ?? 0} onChange={(e) => setF({ ...f, frais_fixes: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><UiLabel>Déductions (€)</UiLabel><Input type="number" value={f.deductions ?? 0} onChange={(e) => setF({ ...f, deductions: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            </div>
            <LocationsEditor value={f.locations} onChange={(v) => setF({ ...f, locations: v })} />
            <label className="flex items-start justify-between gap-3 rounded-2xl bg-violet-50/60 p-3">
              <span><span className="block text-sm font-semibold">Encaisse lui-même CB, espèces et chèques</span><span className="block text-xs text-gray-500">Collaborateur : ces règlements sont déduits à 100 % de sa part. Désactivé par défaut pour le praticien titulaire.</span></span>
              <Switch data-testid="prat-encaisse-direct" checked={!!f.encaisse_direct} onCheckedChange={(v) => setF({ ...f, encaisse_direct: v })} />
            </label>
            <p className="text-xs text-gray-400">La rétrocession porte sur le CA réellement encaissé (CB, espèces, chèques du mois + Sécu/Mutuelles/Alma une fois perçus). Solde = CA × taux − ce que le collaborateur a déjà encaissé − frais.</p>
            <Button data-testid="prat-submit" onClick={save} disabled={saving} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enregistrer"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <MarkReceivedDialog open={!!receiveTx} onOpenChange={(v) => !v && setReceiveTx(null)} transaction={receiveTx} onDone={load} />
    </div>
  );
}
